import { supabase } from './supabaseClient'

// Maximum dimension for the processed avatar. 512px is sharp enough
// for any in-app render (the largest surface uses ~44px) while
// keeping the upload under ~100 KB at q=0.85 JPEG.
const TARGET_SIZE = 512
const JPEG_QUALITY = 0.85

// Read a File / Blob into an HTMLImageElement via an object URL.
// Resolves once decoded so canvas drawImage can use it immediately.
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = (err) => { URL.revokeObjectURL(url); reject(err) }
    img.src = url
  })
}

// Center-square-crop + downscale to TARGET_SIZE × TARGET_SIZE, encoded
// as a JPEG blob. Browser auto-rotates EXIF orientation on decode via
// img.decode(), so we don't need to read the EXIF tag separately.
async function processToSquareJpeg(file) {
  const img = await loadImage(file)
  const srcSize = Math.min(img.naturalWidth, img.naturalHeight)
  const srcX = (img.naturalWidth - srcSize) / 2
  const srcY = (img.naturalHeight - srcSize) / 2

  const canvas = document.createElement('canvas')
  canvas.width = TARGET_SIZE
  canvas.height = TARGET_SIZE
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(
    img,
    srcX, srcY, srcSize, srcSize,
    0, 0, TARGET_SIZE, TARGET_SIZE,
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('canvas.toBlob returned null'))
      },
      'image/jpeg',
      JPEG_QUALITY,
    )
  })
}

// Upload an avatar file for a user. Center-square-crops + downscales
// to 512px JPEG, writes to avatars/<userId>/avatar.jpg, returns a
// fresh public URL with a cache-busting timestamp so the new bytes
// are picked up immediately (Supabase Storage public URLs are
// otherwise edge-cached).
//
// Resolves with { url, error }. On success `url` is the cache-busted
// public URL ready to write to user_profiles.avatar_url. On failure
// `error` carries the original message and `url` is null.
export async function uploadAvatar(userId, file) {
  if (!userId) return { url: null, error: 'missing userId' }
  if (!file) return { url: null, error: 'missing file' }

  let blob
  try {
    blob = await processToSquareJpeg(file)
  } catch (e) {
    return { url: null, error: e?.message || 'image decode failed' }
  }

  const path = `${userId}/avatar.jpg`
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(path, blob, {
      upsert: true,
      contentType: 'image/jpeg',
      cacheControl: '300',
    })
  if (uploadError) return { url: null, error: uploadError.message }

  const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path)
  const base = pub?.publicUrl
  if (!base) return { url: null, error: 'no publicUrl returned' }
  const url = `${base}?v=${Date.now()}`
  return { url, error: null }
}
